import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import TankList from './features/tankList';
import {
  clearFilter,
  fetchClinic,
  fetchNotifications,
  fetchTanks,
  toggleFilter,
  updateNotification,
  reset,
  changeRightBarType,
  unsnoozeTank,
} from './_slice';
import { useAppDispatch, useAppSelector } from '../../hook';
import appConfig from '../../config.app';
import {
  VNotificationCategoryEnum,
  VNotificationStatusEnum,
  VNotificationTypeEnum,
  VTank,
} from '../../services';
import NotificationList from './features/notificationList';
import TankFilter from './features/tankFilter';
import Layout2 from '../../layouts/layout2';
import ClinicTopBar from './features/clinicTopBar';
import Breadcrumbs from '../../components/breadcrumbs';
import { EnumRightBarType } from '../../common/models';
import SnoozeDialog from './features/snoozeDialog';
import AlarmList from './features/alarmList';
import { getAlarmCount } from '../../common/helper';

interface RouteParams {
  clinicId: string;
  [key: string]: string;
}

const ClinicDashboard: React.FC = () => {
  const dashboardState = useAppSelector((state) => state.dashboard);
  const dispatch = useAppDispatch();
  const { clinicId } = useParams<RouteParams>();
  const [alarmToSnooze, setAlarmToSnooze] = React.useState<{
    tank: VTank;
    notificationType: VNotificationTypeEnum;
  }>();

  useEffect(() => {
    dispatch(reset());
    dispatch(fetchClinic({ clinicId: clinicId as string }));
    dispatch(fetchTanks({ clinicId: clinicId as string, needLoading: true }));
    dispatch(fetchNotifications({ clinicId: clinicId as string }));
    const intervalId = setInterval(() => {
      dispatch(fetchTanks({ clinicId: clinicId as string }));
      dispatch(fetchNotifications({ clinicId: clinicId as string }));
    }, appConfig.pollIntervalSeconds * 1000);

    return () => clearInterval(intervalId);
  }, [clinicId, dispatch]);

  return (
    <Layout2
      breadcrumbsSlot={
        <Breadcrumbs
          breadcrumbs={[{ label: 'Clinics', path: '/clinics' }]}
          title={dashboardState.clinic ? dashboardState.clinic.name : ''}
        />
      }
      contentSlot={
        <TankList
          clinicId={clinicId}
          tanks={dashboardState.filteredTanks}
          status={dashboardState.tanksLoadingStatus}
        />
      }
      topSlot={
        <ClinicTopBar
          clinic={ dashboardState.clinic }
          rightBarType={ dashboardState.rightBarType }
          alarmCount={getAlarmCount(dashboardState.filteredTanks)}
          onChangeRightBarType={(x) => dispatch(changeRightBarType(x))}
        />
      }
      leftSlot={
        <TankFilter
          total={dashboardState.tanks.length}
          statusFilter={ dashboardState.statusFilter }
          onToggleFilter={ (key) => { dispatch(toggleFilter(key)); } }
          onClearFilter={ () => { dispatch(clearFilter()); } }
        />
      }
      rightSlot1Hide={dashboardState.rightBarType !== EnumRightBarType.Alarms}
      rightSlot1={
        <AlarmList
          style={{}}
          tanks={dashboardState.filteredTanks}
          onSnooze={(tank, notificationType) => setAlarmToSnooze({ tank, notificationType })}
          onUnsnooze={
            (_tankId, notificationType) => {
              dispatch(unsnoozeTank({ tankId: _tankId, notificationType }));
            }
          }
        />
      }
      rightSlot2Hide={dashboardState.rightBarType !== EnumRightBarType.History}
      rightSlot2={
        <NotificationList
          onDismiss={
            (notificationId) => dispatch(
              updateNotification({
                notificationId,
                status: VNotificationStatusEnum.Dismissed,
              }),
            )
          }
          onSwitchStatus={
            (notificationId, status) => dispatch(
              updateNotification({
                notificationId,
                status,
              }),
            )
          }
          style={{}}
          notifications={dashboardState.notifications}
        />
      }
      dialogSlotShow={alarmToSnooze !== undefined}
      dialogSlot={
        alarmToSnooze && <SnoozeDialog
          tankId={alarmToSnooze.tank.id}
          tankName={alarmToSnooze.tank.name}
          notificationCategory={VNotificationCategoryEnum.Alarming}
          notificationType={alarmToSnooze.notificationType}
          onCancel={() => setAlarmToSnooze(undefined)}
          onConfirm={() => setAlarmToSnooze(undefined)}
        />
      }
    />
  );
};

export default ClinicDashboard;
